// UserScreen.js — Data Table refactor

import axios from "axios";
import React, { useState, useEffect, useMemo } from "react";
import { API_IP } from "../env";
import PatientsForm from "./Form/PatientsForm";

import Dropdown from "react-bootstrap/Dropdown";
import "./UserScreen.css";

const FILTERS = [
  { id: "all", label: "All", icon: "bi-grid" },
  { id: "verified", label: "Verified", icon: "bi-patch-check" },
  { id: "unverified", label: "Unverified", icon: "bi-clock-history" },
  { id: "deactivated", label: "Deactivated", icon: "bi-slash-circle" },
];

function UserScreen({ globalSearch = "" }) {
  const [userData, setUserData] = useState([]);
  const [showPatientForm, setShowPatientForm] = useState(false);
  const [selectedAccountID, setSelectedAccountID] = useState();
  const [selectedUsername, setSelectedUsername] = useState();
  const [refreshUser, setRefreshUser] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [paginationInfo, setPaginationInfo] = useState({
    currentPage: 1, pageSize: 10, totalItems: 0, totalPages: 1,
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  async function axiosReqConfig(url, method, data) {
    const bearerToken = localStorage.getItem("@uphealth_login_token");
    return await axios({
      url, method,
      headers: { Authorization: `Bearer ${bearerToken}` },
      data,
    });
  }

  async function getAllUsers(page = 1, search = "") {
    setLoading(true);
    try {
      const { data } = await axiosReqConfig(
        `${API_IP}/api/admin/users?page=${page}&limit=10&search=${encodeURIComponent(search)}`,
        "get",
        []
      );
      if (data && data.pagination) {
        setUserData(data.data);
        setPaginationInfo(data.pagination);
      } else {
        setUserData(data);
      }
    } catch (err) {
      console.error("Failed to fetch users", err);
    }
    setLoading(false);
  }

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearchTerm(searchTerm), 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    const effective = debouncedSearchTerm || globalSearch;
    getAllUsers(1, effective);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshUser, debouncedSearchTerm, globalSearch]);

  const handleOpenPatients = (item) => {
    setSelectedAccountID(item.account_id);
    setSelectedUsername(item.username);
    setShowPatientForm(true);
  };

  const updateAccountStatus = async (item, input) => {
    let inputStatus, statusText;
    if (input === 0) { inputStatus = true; statusText = "VERIFY"; }
    else if (input === 1) { inputStatus = false; statusText = "UNVERIFY"; }
    else { inputStatus = null; statusText = "DEACTIVATE"; }

    if (window.confirm(`Are you sure you want to ${statusText} ${item.username}?`)) {
      await axiosReqConfig(`${API_IP}/api/admin/users/${item.account_id}`, "patch", {
        updateSection: 0, updateInfo: inputStatus,
      });
      setRefreshUser((r) => !r);
    }
  };

  const getUserStatus = (u) => {
    if (u.verification === true) return { id: "verified", label: "Verified", cls: "dt-pill--success" };
    if (u.verification === false) return { id: "unverified", label: "Unverified", cls: "dt-pill--warn" };
    return { id: "deactivated", label: "Deactivated", cls: "dt-pill--danger" };
  };

  const counts = useMemo(() => {
    const c = { all: userData.length, verified: 0, unverified: 0, deactivated: 0 };
    userData.forEach((u) => { c[getUserStatus(u).id]++; });
    return c;
  }, [userData]);

  const filteredUsers = useMemo(() => {
    if (filter === "all") return userData;
    return userData.filter((u) => getUserStatus(u).id === filter);
  }, [userData, filter]);

  const handleNextPage = () => {
    if (paginationInfo.currentPage < paginationInfo.totalPages) {
      getAllUsers(paginationInfo.currentPage + 1, debouncedSearchTerm || globalSearch);
    }
  };
  const handlePreviousPage = () => {
    if (paginationInfo.currentPage > 1) {
      getAllUsers(paginationInfo.currentPage - 1, debouncedSearchTerm || globalSearch);
    }
  };

  const avatarColor = (name) => {
    const palette = ["dt-avatar--teal", "dt-avatar--violet", "dt-avatar--blue", ""];
    const i = (name || "").charCodeAt(0) % palette.length;
    return palette[i];
  };

  return (
    <>
      <div className="user-management-container">
        {/* ── Page Header ── */}
        <div className="page-header">
          <div>
            <h1 className="page-header__title">Users</h1>
            <div className="page-header__subtitle">
              {loading
                ? "Loading users…"
                : <>Showing <strong style={{ color: "var(--text-1)" }}>{filteredUsers.length}</strong> on this page · <strong style={{ color: "var(--text-1)" }}>{paginationInfo.totalItems}</strong> total accounts</>}
            </div>
          </div>
          {/* <div className="page-header__right">
            <button className="ov-quick-btn">
              <i className="bi bi-download" /> Export
            </button>
            <button className="ov-quick-btn ov-quick-btn--accent">
              <i className="bi bi-plus-lg" /> Invite User
            </button>
          </div> */}
        </div>

        {/* ── Toolbar ── */}
        <div className="dt-toolbar">
          <div className="dt-toolbar__left">
            <div className="dt-filters">
              {FILTERS.map((f) => (
                <button
                  key={f.id}
                  className={`dt-filter-pill ${filter === f.id ? "dt-filter-pill--active" : ""}`}
                  onClick={() => setFilter(f.id)}
                >
                  <i className={`bi ${f.icon}`} />
                  {f.label}
                  <span className="dt-filter-pill__count">{counts[f.id] || 0}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="dt-toolbar__right">
            <div className="dt-search">
              <i className="bi bi-search dt-search__icon" />
              <input
                type="text"
                placeholder="Search username, email, phone…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* ── User Table ── */}
        <div className="dt-shell">
          {loading ? (
            <div className="dt-loading">
              <div className="spinner-border" />
              <span>Loading users…</span>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="dt-empty">
              <div className="dt-empty__icon"><i className="bi bi-person-x" /></div>
              <h4>No users found</h4>
              <p>Try adjusting your search or filter.</p>
            </div>
          ) : (
            <table className="dt-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Status</th>
                  <th>Phone</th>
                  <th>Account ID</th>
                  <th className="dt-th--right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((u) => {
                  const status = getUserStatus(u);
                  const initials = (u.username || "?").slice(0, 2).toUpperCase();

                  return (
                    <tr key={u.account_id}>
                      <td>
                        <div className="dt-user">
                          <div className={`dt-avatar ${avatarColor(u.username)}`}>
                            {initials}
                          </div>
                          <div className="dt-cell-stack">
                            <div className="dt-name">{u.username}</div>
                            <div className="dt-sub">{u.email}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <Dropdown>
                          <Dropdown.Toggle as="button" className="us-status-toggle" id={`u-${u.account_id}`}>
                            <span className={`dt-pill ${status.cls}`}>{status.label}</span>
                            <i className="bi bi-chevron-down us-status-chev" />
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            <Dropdown.Item onClick={() => updateAccountStatus(u, 0)}>
                              <i className="bi bi-patch-check me-2" style={{ color: "var(--green)" }} />
                              Set as Verified
                            </Dropdown.Item>
                            <Dropdown.Item onClick={() => updateAccountStatus(u, 1)}>
                              <i className="bi bi-clock-history me-2" style={{ color: "var(--amber)" }} />
                              Set as Unverified
                            </Dropdown.Item>
                            <Dropdown.Item onClick={() => updateAccountStatus(u, 2)}>
                              <i className="bi bi-slash-circle me-2" style={{ color: "var(--red)" }} />
                              Deactivate
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </td>
                      <td>
                        <span style={{ color: "var(--text-2)", fontSize: "0.83rem" }}>
                          {u.phone_number || <span style={{ color: "var(--text-3)" }}>—</span>}
                        </span>
                      </td>
                      <td>
                        <span className="dt-mono">#{u.account_id}</span>
                      </td>
                      <td className="dt-td--right">
                        <div className="ps-row-actions">
                          <button
                            className="dt-action-btn dt-action-btn--accent"
                            title="View Patients"
                            onClick={() => handleOpenPatients(u)}
                          >
                            <i className="bi bi-people" />
                          </button>
                          <div className="dropdown" style={{ display: "inline-block" }}>
                            <button
                              className="dt-action-btn"
                              data-bs-toggle="dropdown"
                              aria-expanded="false"
                              title="More"
                            >
                              <i className="bi bi-three-dots" />
                            </button>
                            <ul className="dropdown-menu action-menu-dark dropdown-menu-end">
                              <li>
                                <button className="dropdown-item" onClick={() => handleOpenPatients(u)}>
                                  <i className="bi bi-people me-2" /> View Patients
                                </button>
                              </li>
                              <li>
                                <button className="dropdown-item" onClick={() => alert("Email sent — coming soon")}>
                                  <i className="bi bi-envelope me-2" /> Contact User
                                </button>
                              </li>
                              <li><hr className="dropdown-divider" /></li>
                              <li>
                                <button className="dropdown-item" onClick={() => updateAccountStatus(u, 2)}>
                                  <i className="bi bi-slash-circle me-2" /> Deactivate
                                </button>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {!loading && filteredUsers.length > 0 && (
            <div className="dt-footer">
              <div>
                Page <span className="dt-pager-info">{paginationInfo.currentPage}</span> of {paginationInfo.totalPages}
                <span style={{ margin: "0 10px", color: "var(--border-3)" }}>·</span>
                <span className="dt-pager-info">{paginationInfo.totalItems}</span> total
              </div>
              <div className="dt-footer__pager">
                <button className="dt-pager-btn" onClick={handlePreviousPage}
                  disabled={paginationInfo.currentPage <= 1 || loading}>
                  <i className="bi bi-chevron-left" />
                </button>
                <button className="dt-pager-btn" onClick={handleNextPage}
                  disabled={paginationInfo.currentPage >= paginationInfo.totalPages || loading}>
                  <i className="bi bi-chevron-right" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <PatientsForm
        show={showPatientForm}
        onHide={() => setShowPatientForm(false)}
        accountID={selectedAccountID}
        username={selectedUsername}
      />
    </>
  );
}

export default UserScreen;
